import { Assets, Container, Sprite, Ticker, type Texture } from 'pixi.js';
import type {
  ItemId,
  WheelActionCard,
  WheelObservableState,
} from '@goodtime/shared';
import { AudioBus } from '../audio/AudioBus';
import { Easings, TweenGroup } from '../core/Tween';
import { SceneBase } from './SceneBase';
import { WheelResolveBonusMarker } from '../widgets/wheel/WheelResolveBonusMarker';
import { WheelResolveBoostTelegraph } from '../widgets/wheel/WheelResolveBoostTelegraph';
import { WheelResolveConfetti } from '../widgets/wheel/WheelResolveConfetti';
import {
  WheelResolveDeployStrip,
  type DeployStripTileData,
} from '../widgets/wheel/WheelResolveDeployStrip';
import { WheelResolveDisc } from '../widgets/wheel/WheelResolveDisc';
import { WheelResolveHub } from '../widgets/wheel/WheelResolveHub';
import { WheelResolveOutcomeChip } from '../widgets/wheel/WheelResolveOutcomeChip';
import {
  WheelResolveCentralLedger,
  buildLedgerLineItems,
  projectPodiumRank,
  reconcileLedgerSubtotal,
  renderServerLedgerLines,
} from '../widgets/wheel/WheelResolveCentralLedger';
import {
  WheelResolveSeatFx,
  type SeatFxPlayConfig,
} from '../widgets/wheel/WheelResolveSeatFx';
import { WheelResolveAttackFx } from '../widgets/wheel/WheelResolveAttackFx';
import {
  WheelResolveSeatPointer,
  type WheelResolveSeatPointerState,
} from '../widgets/wheel/WheelResolveSeatPointer';
import { WheelResolveStakeRing } from '../widgets/wheel/WheelResolveStakeRing';
import { WheelRimLights } from '../widgets/wheel/WheelRimLights';

/**
 * Multiplayer wheel-resolve scene — owns every visual layer of
 * the WHEEL_RESOLVE phase on a single Pixi canvas. Producer brief
 * 2026-05-16: the wheel-resolve view was previously 100% React /
 * DOM + CSS (3.4k lines of TSX); this scene takes over end-to-end
 * so the canvas owns the spin physics, action-card sequencer,
 * outcome chips, per-avatar FX, and post-spin confetti.
 *
 * <h2>Layering (back → front)</h2>
 *
 * <ol>
 *   <li>Disc + wedges (rotating)</li>
 *   <li>Outer rim PNG (static)</li>
 *   <li>Rim lights chase animation</li>
 *   <li>Hub (countdown + Let's Go! text)</li>
 *   <li>STAKE clock-fill ring around the orbit</li>
 *   <li>Perimeter seat avatars</li>
 *   <li>BOOST telegraph badge (self-seat radial offset)</li>
 *   <li>Per-avatar SeatItemFx overlays</li>
 *   <li>Outcome chips (post-spin fan-out reveal)</li>
 *   <li>Centre action-card overlay (sequencer flashes)</li>
 *   <li>Confetti burst</li>
 * </ol>
 *
 * <h2>State intake</h2>
 *
 * <p>The host React component pushes the latest
 * {@link WheelObservableState} via {@link setState}; the scene
 * compares against its prior snapshot and runs only what changed.
 * Item icons + avatar PNGs come in as URL maps and are resolved
 * via {@code Assets.load} (cached per URL).
 */

const SPIN_DURATION_MS = 7000;
const SPIN_REVOLUTIONS = 6;
const STAKE_WINDOW_MS = 10_000;
// Producer feedback 2026-05-17 — "slow the action card cycle, you
// can't read them." 700 ms was below the legibility threshold for
// the card body copy ("Bea bombed Carl · −60" etc.). 1800 ms gives
// the player time to parse name + verb + magnitude before the next
// card lands.
const ACTION_CARD_DURATION_MS = 1800;
const CHIP_REVEAL_STAGGER_MS = 220;
const SEAT_POINTER_OFFSETS_DEG = [0, 60, 120, 180, 240, 300] as const;

export interface WheelResolveSceneOptions {
  hubTextureUrl: string;
  rimTextureUrl: string;
  /** Optional gold pointer asset rendered on the local player's
   *  seat, tip pointing at the wheel centre. Producer feedback
   *  2026-05-17. */
  pointerTextureUrl?: string;
  /** Optional coin glyph for the seat-fx win/loss floater so the
   *  gain/loss reads as currency at a glance. */
  coinTextureUrl?: string;
  /** Optional high-five glyph for the per-seat gesture badge
   *  (producer 2026-06-08). When supplied, each non-self occupant
   *  gets a tappable high-five badge during the spectating phases;
   *  taps fire {@link WheelResolveSceneCallbacks.onSeatGesture}. */
  highFiveTextureUrl?: string;
}

export type AvatarUrlMap = Map<number, string>;
export type ItemIconUrlMap = Map<ItemId, string>;

export interface WheelResolveSceneCallbacks {
  onSeatTap?: (slot: number) => void;
  /** Fires when the player taps a loadout
   *  tile to use it. Host submits {@code DEPLOY_ITEM} with the itemId. */
  onDeployItem?: (itemId: ItemId) => void;
  /** Fires when the player taps a seat's high-five badge. Host
   *  resolves the occupant for the slot and submits a directed
   *  {@code REACTION} (high_five) at that seat. */
  onSeatGesture?: (slot: number) => void;
}

interface SeatFxRecord {
  fx: WheelResolveSeatFx;
  card: WheelActionCard;
}

export class WheelResolveScene extends SceneBase {
  private disc: WheelResolveDisc | null = null;
  private hub: WheelResolveHub | null = null;
  private rim: WheelRimLights | null = null;
  private rimSprite: Sprite | null = null;
  private stakeRing: WheelResolveStakeRing | null = null;
  // Central action-card overlay retired 2026-05-27. Sequencing
  // logic (actionCardQueue + playNextActionCard) is preserved so
  // the seat fx still play one card at a time.
  private confetti: WheelResolveConfetti | null = null;
  private deployStrip: WheelResolveDeployStrip | null = null;
  private boostTelegraph: WheelResolveBoostTelegraph | null = null;
  private boostTelegraphHost: WheelResolveSeatPointer | null = null;
  private seatContainer: Container | null = null;
  private fxContainer: Container | null = null;
  /** Stage-wide attack motion (lightning bolts, impact bursts) drawn
   *  above the per-seat fx. */
  private attackFx: WheelResolveAttackFx | null = null;
  /** Screen-shake animation frame, and the stage position to restore. */
  private shakeRaf: number | null = null;
  private chipContainer: Container | null = null;
  private bonusMarkerHost: Container | null = null;
  private readonly seatPointers: WheelResolveSeatPointer[] = [];
  /** Personal end-of-spin breakdown, centred over the hub.
   *  Replaces the perimeter chip grid per producer feedback
   *  2026-05-27. */
  private centralLedger: WheelResolveCentralLedger | null = null;
  private readonly outcomeChips: Map<number, WheelResolveOutcomeChip> =
    new Map();
  private readonly activeSeatFx: Map<number, SeatFxRecord> = new Map();
  private readonly bonusMarkers: WheelResolveBonusMarker[] = [];

  private latestState: WheelObservableState | null = null;
  private prevPhase: string | null = null;
  private avatarUrls: AvatarUrlMap = new Map();
  private avatarTextures: Map<number, Texture> = new Map();
  private itemIconUrls: ItemIconUrlMap = new Map();
  private itemIconTextures: Map<ItemId, Texture> = new Map();
  private callbacks: WheelResolveSceneCallbacks;
  private hubTextureUrl: string;
  private rimTextureUrl: string;
  private pointerTextureUrl: string | null;
  private coinTextureUrl: string | null;
  private highFiveTextureUrl: string | null;
  private hubTexture: Texture | null = null;
  private rimTexture: Texture | null = null;
  private pointerTexture: Texture | null = null;
  private coinTexture: Texture | null = null;
  private highFiveTexture: Texture | null = null;
  /** Active high-five floats — a gesture sprite rising + fading over a
   *  seat (the wheel-game equivalent of the reel race's ReactionFloat,
   *  producer 2026-06-08 "animate the hand"). Advanced each ticker frame
   *  in {@link #tickerFn}; entries self-remove when faded out. */
  private gestureFloats: { sprite: Sprite; elapsed: number; baseY: number }[] = [];
  /** Last avatar diameter from layout — sizes the gesture float. */
  private lastAvatarDiameter = 56;
  private readonly tweens = new TweenGroup();
  private stakeStartMs = 0;
  private lastMatchNumber: number | null = null;
  private spinStarted = false;
  /** Outcome chips visible only after the wheel lands. Set true
   *  inside {@link startSpin}'s onComplete. */
  private chipsRevealed = false;
  private actionCardQueue: WheelActionCard[] = [];
  private actionCardTimer: number | null = null;
  private tickerFn: ((t: Ticker) => void) | null = null;
  private selfSeatId: number | null = null;

  constructor(
    container: HTMLElement,
    options: WheelResolveSceneOptions,
    callbacks: WheelResolveSceneCallbacks = {},
  ) {
    super(container, { drawDefaultBackground: false });
    this.callbacks = callbacks;
    this.hubTextureUrl = options.hubTextureUrl;
    this.rimTextureUrl = options.rimTextureUrl;
    this.pointerTextureUrl = options.pointerTextureUrl ?? null;
    this.coinTextureUrl = options.coinTextureUrl ?? null;
    this.highFiveTextureUrl = options.highFiveTextureUrl ?? null;
  }

  protected async preload(): Promise<void> {
    const [hub, rim, pointer, coin, highFive] = await Promise.all([
      Assets.load<Texture>(this.hubTextureUrl).catch(() => null),
      Assets.load<Texture>(this.rimTextureUrl).catch(() => null),
      this.pointerTextureUrl
        ? Assets.load<Texture>(this.pointerTextureUrl).catch(() => null)
        : Promise.resolve(null),
      this.coinTextureUrl
        ? Assets.load<Texture>(this.coinTextureUrl).catch(() => null)
        : Promise.resolve(null),
      this.highFiveTextureUrl
        ? Assets.load<Texture>(this.highFiveTextureUrl).catch(() => null)
        : Promise.resolve(null),
    ]);
    this.hubTexture = hub ?? null;
    this.rimTexture = rim ?? null;
    this.pointerTexture = pointer ?? null;
    this.coinTexture = coin ?? null;
    this.highFiveTexture = highFive ?? null;
  }

  protected buildContent(): void {
    const stage = this.app.stage;

    this.disc = new WheelResolveDisc({ diameter: 100 });
    stage.addChild(this.disc);

    // Bonus markers live inside the disc's rotating overlay so
    // they stay pinned to their wedge as the wheel spins. The
    // disc's setRotation rotates rotatingOverlay too — each
    // marker counter-rotates its own contents to stay upright.
    this.bonusMarkerHost = this.disc.rotatingOverlay;

    if (this.rimTexture) {
      this.rimSprite = new Sprite(this.rimTexture);
      this.rimSprite.anchor.set(0.5);
      stage.addChild(this.rimSprite);
    }

    this.rim = new WheelRimLights({ diameter: 100, count: 16, cycleMs: 1600 });
    stage.addChild(this.rim);

    this.hub = new WheelResolveHub({
      diameter: 60,
      hubTexture: this.hubTexture,
    });
    stage.addChild(this.hub);

    this.stakeRing = new WheelResolveStakeRing({
      diameter: 100,
      strokeWidth: 4,
    });
    this.stakeRing.visible = false;
    stage.addChild(this.stakeRing);

    // Seat-pointer + telegraph diameters use the construction-time
    // fallback (mobile) — the host's onResize will rebuild them at
    // the live dim on first layout. We can't read the renderer
    // here yet (init not finished); the placeholder is harmless
    // since the first layout pass is synchronous.
    const fallbackAvatarDiameter = 56;
    this.seatContainer = new Container();
    stage.addChild(this.seatContainer);
    for (let i = 0; i < 6; i++) {
      const seat = new WheelResolveSeatPointer({
        diameter: fallbackAvatarDiameter,
        pointerTexture: this.pointerTexture,
        highFiveTexture: this.highFiveTexture,
      });
      const slot = i + 1;
      seat.setOnTap(() => this.callbacks.onSeatTap?.(slot));
      seat.setOnGesture(() => {
        // Immediate local feedback — the hand rises + fades over the
        // target avatar, mirroring the reel race's ReactionFloat.
        this.playGestureFloat(slot, this.highFiveTexture);
        this.callbacks.onSeatGesture?.(slot);
      });
      this.seatContainer.addChild(seat);
      this.seatPointers.push(seat);
    }

    // BOOST telegraph — attached to the self-seat's container,
    // positioned via setRadialOffset(angle).
    this.boostTelegraph = new WheelResolveBoostTelegraph({
      avatarDiameter: fallbackAvatarDiameter,
    });
    this.boostTelegraph.visible = false;

    this.fxContainer = new Container();
    stage.addChild(this.fxContainer);

    this.attackFx = new WheelResolveAttackFx();
    stage.addChild(this.attackFx);

    this.chipContainer = new Container();
    // Producer feedback 2026-05-27 — perimeter chips replaced by
    // the central ledger; keep the container + chip widgets alive
    // for safety (e.g. audience mode where the ledger doesn't
    // render, or themes that opt back in), but the container stays
    // INVISIBLE by default.
    this.chipContainer.visible = false;
    stage.addChild(this.chipContainer);
    for (let i = 0; i < 6; i++) {
      const chip = new WheelResolveOutcomeChip({
        minWidth: 110,
        baseFontSize: 14,
      });
      this.chipContainer.addChild(chip);
      this.outcomeChips.set(i + 1, chip);
    }

    this.centralLedger = new WheelResolveCentralLedger({
      width: 280,
      coinTexture: this.coinTexture,
    });
    stage.addChild(this.centralLedger);

    // Producer feedback 2026-05-27 — central action card retired.
    // The per-seat fx (icon on victim + coin floater on attacker &
    // victim) carries the narrative now, so the centred dialogue
    // was duplicating information. The actionCardQueue sequencing
    // is preserved so the seat-fx beats still play one card at a
    // time.

    this.confetti = new WheelResolveConfetti();
    stage.addChild(this.confetti);

    this.deployStrip = new WheelResolveDeployStrip({
      onFire: (itemId) => this.callbacks.onDeployItem?.(itemId),
    });
    this.deployStrip.visible = false;
    stage.addChild(this.deployStrip);

    // Producer brief 2026-05-16 — if setState() arrived before
    // buildContent finished (Pixi init is async), replay the
    // latest state now so the disc + seats catch up. Without this
    // the wheel renders blank for the first STAKE / WHEEL_RESOLVE
    // tick after a fresh React mount.
    if (this.latestState) {
      this.setState(this.latestState, this.selfSeatId);
    }

    this.tickerFn = (t: Ticker) => {
      const deltaMs = t.deltaMS;
      this.rim?.tick(deltaMs);
      for (const seat of this.seatPointers) seat.tick(deltaMs);
      this.confetti?.tick(deltaMs);
      this.deployStrip?.tick(deltaMs);

      // Gesture floats — rise + fade, then reap. ~1.1s mirrors the reel
      // race's ReactionFloat lifetime. Iterate backwards so splice is safe.
      if (this.gestureFloats.length > 0) {
        const rise = this.lastAvatarDiameter * 0.9;
        for (let i = this.gestureFloats.length - 1; i >= 0; i--) {
          const f = this.gestureFloats[i]!;
          f.elapsed += deltaMs;
          const p = Math.min(1, f.elapsed / 1100);
          f.sprite.y = f.baseY - p * rise;
          f.sprite.alpha = 1 - p * p;
          if (p >= 1) {
            f.sprite.destroy();
            this.gestureFloats.splice(i, 1);
          }
        }
      }

      // Hub countdown + stake ring progress driven from the STAKE
      // start wall-clock so they stay in sync.
      if (this.latestState?.phase === 'STAKE') {
        const elapsed = performance.now() - this.stakeStartMs;
        const remaining = Math.max(0, STAKE_WINDOW_MS - elapsed) / 1000;
        this.hub?.setCountdown(remaining);
        const progress = Math.min(1, elapsed / STAKE_WINDOW_MS);
        if (this.stakeRing && !this.stakeRing.visible) {
          this.stakeRing.visible = true;
        }
        this.stakeRing?.setProgress(progress);
      } else if (this.stakeRing?.visible) {
        this.stakeRing.visible = false;
      }
    };
    this.app.ticker.add(this.tickerFn);
  }

  protected disposeResources(_wasInitialized: boolean): void {
    if (this.tickerFn) {
      this.app.ticker?.remove(this.tickerFn);
      this.tickerFn = null;
    }
    if (this.actionCardTimer !== null) {
      window.clearTimeout(this.actionCardTimer);
      this.actionCardTimer = null;
    }
    this.tweens.cancelAll();
    for (const f of this.gestureFloats) f.sprite.destroy();
    this.gestureFloats = [];
    this.disc?.destroyDisplay();
    this.hub?.destroyDisplay();
    this.rim?.removeChildren();
    this.stakeRing?.destroyDisplay();
    this.centralLedger?.destroyDisplay();
    this.confetti?.destroyDisplay();
    this.deployStrip?.destroyDisplay();
    this.boostTelegraph?.destroyDisplay();
    this.rimSprite?.destroy();
    for (const seat of this.seatPointers) seat.destroyDisplay();
    for (const chip of this.outcomeChips.values()) chip.destroyDisplay();
    for (const rec of this.activeSeatFx.values()) rec.fx.destroyDisplay();
    for (const marker of this.bonusMarkers) marker.destroyDisplay();
    this.stopShake();
    this.attackFx?.destroyDisplay();
    this.attackFx = null;
  }

  protected onResize(): void {
    if (!this.disc || !this.hub || !this.rim) return;
    const { width, height } = this.app.renderer;
    const cx = width / 2;
    const cy = height / 2;
    const dims = this.pickDims(width, height);
    this.layoutSurface(cx, cy, dims);
    this.refreshSeatPositions();
    this.refreshChipPositions();
    this.deployStrip?.layout(width, height);
  }

  /** Resolve the wheel-sizing tier from the canvas dimensions.
   *  Mirrors the legacy {@code useWheelDims} table.
   *
   *  <p>Producer brief 2026-05-16 — Pixi 8 with autoDensity:true
   *  reports {@code renderer.width} as CSS pixels (not internal
   *  pixels — Pixi handles the resolution multiplication
   *  internally). Stage drawing coordinates ARE CSS pixels too.
   *  So the picker reads renderer.width directly and returns
   *  CSS-pixel sizing 1:1 with the legacy {@code useWheelDims}
   *  table. Earlier versions of this method incorrectly
   *  multiplied / divided by resolution, which forced the picker
   *  onto the MOBILE tier at all viewports. */
  private pickDims(width: number, height: number): {
    diameter: number;
    avatarDiameter: number;
    rimGap: number;
    orbitRadius: number;
    hubSize: number;
    wedgeLabelFontSize: number;
    wedgeLabelRadius: number;
    chipMinWidth: number;
    chipFontSize: number;
  } {
    const vw = width;
    void height;
    // Producer feedback 2026-05-17 ("Avatars still aren't positioned
    // quite right"): the rim sprite's gold band extends ~25-30px
    // past the disc edge, and the legacy orbitRadius = discRadius +
    // small rimGap had the avatars overlapping the rim significantly.
    // Bump the orbit so the avatar bottom edge sits clear of the
    // rim's outer edge — they now read as sitting AROUND the wheel
    // rather than ON it.
    if (vw < 420) {
      const wheelSize = 260;
      return {
        diameter: wheelSize,
        avatarDiameter: 42,
        rimGap: 32,
        orbitRadius: wheelSize / 2 + 32,
        hubSize: 96,
        wedgeLabelFontSize: 13,
        // Pushed toward the wide (rim) end of each wedge so the radial
        // numbers aren't squashed near the narrow centre (producer
        // 2026-06-10).
        wedgeLabelRadius: wheelSize / 2 - 22,
        chipMinWidth: 64,
        chipFontSize: 11,
      };
    }
    if (vw < 880) {
      const wheelSize = 360;
      return {
        diameter: wheelSize,
        avatarDiameter: 56,
        rimGap: 44,
        orbitRadius: wheelSize / 2 + 44,
        hubSize: 128,
        wedgeLabelFontSize: 15,
        wedgeLabelRadius: wheelSize / 2 - 30,
        chipMinWidth: 76,
        chipFontSize: 12,
      };
    }
    const wheelSize = 460;
    return {
      diameter: wheelSize,
      avatarDiameter: 68,
      rimGap: 54,
      orbitRadius: wheelSize / 2 + 54,
      hubSize: 152,
      wedgeLabelFontSize: 17,
      wedgeLabelRadius: wheelSize / 2 - 40,
      chipMinWidth: 88,
      chipFontSize: 13,
    };
  }

  setState(state: WheelObservableState, selfSeatId: number | null): void {
    this.latestState = state;
    this.selfSeatId = selfSeatId;

    if (state.matchNumber !== this.lastMatchNumber) {
      this.lastMatchNumber = state.matchNumber;
      this.stakeStartMs = performance.now();
      this.spinStarted = false;
      this.chipsRevealed = false;
      this.disc?.setRotation(0);
      this.hub?.clear();
      // Hide any lingering chips + FX from the prior match.
      for (const chip of this.outcomeChips.values()) chip.hideImmediate();
      this.centralLedger?.hide();
      this.clearAllSeatFx();
      this.clearActionCardQueue();
      this.confetti?.destroyDisplay();
    }

    // Phase transition: hide stuff that's phase-specific.
    if (state.phase !== this.prevPhase) {
      if (this.prevPhase === 'STAKE' && state.phase !== 'STAKE') {
        // STAKE → next phase: hide telegraph + clear seat FX from
        // the prior stake's intent.
        if (this.boostTelegraph) this.boostTelegraph.visible = false;
      }
      this.prevPhase = state.phase;
    }

    this.disc?.setDial(state.currentDial ?? null);
    this.refreshBonusMarkers(state, selfSeatId);

    if (state.phase === 'STAKE') {
      // Countdown ring driven from ticker.
      this.refreshBoostTelegraph(state, selfSeatId);
      this.refreshDeployStrip(state, selfSeatId);
      if (this.deployStrip) this.deployStrip.visible = true;
    } else if (
      state.phase === 'WHEEL_RESOLVE' ||
      state.phase === 'WHEEL_REVEAL'
    ) {
      this.hub?.showLetsGo();
      if (this.deployStrip) {
        this.deployStrip.visible = false;
        this.deployStrip.resetArmedAndFiring();
      }
      if (!this.spinStarted && state.dialResolveResult) {
        // Producer feedback 2026-05-26 — "the wheel just sits". Root
        // cause: setState can fire BEFORE the async scene.init() has
        // created this.disc, in which case startSpin bails early. We
        // used to flip spinStarted=true here regardless, which then
        // blocked the retry when buildContent replayed setState. Let
        // startSpin own the flip so a no-disc bail stays retryable.
        this.startSpin(state);
      }
    } else {
      this.hub?.clear();
      if (this.boostTelegraph) this.boostTelegraph.visible = false;
      if (this.deployStrip) {
        this.deployStrip.visible = false;
        this.deployStrip.resetArmedAndFiring();
      }
    }

    this.refreshSeats(state, selfSeatId);
  }

  setAvatarUrls(urls: AvatarUrlMap, selfSeatId: number | null): void {
    this.avatarUrls = urls;
    void this.refreshAvatarTextures(selfSeatId);
  }

  /** Host pushes the item-icon URL map (item:ID → PNG URL). Used
   *  by SeatItemFx to render the right icon per action card. */
  setItemIconUrls(urls: ItemIconUrlMap): void {
    this.itemIconUrls = urls;
    void this.refreshItemIconTextures();
  }

  // ---- internal: textures ----------------------------------------------

  private async refreshAvatarTextures(
    selfSeatId: number | null,
  ): Promise<void> {
    let changed = false;
    for (const [seatId, url] of this.avatarUrls.entries()) {
      if (this.avatarTextures.has(seatId)) continue;
      try {
        const tex = await Assets.load<Texture>(url);
        this.avatarTextures.set(seatId, tex);
        changed = true;
      } catch {
        // Fallback initial covers it.
      }
    }
    if (changed && this.latestState) {
      this.refreshSeats(this.latestState, selfSeatId);
      this.refreshBonusMarkers(this.latestState, selfSeatId);
    }
  }

  private async refreshItemIconTextures(): Promise<void> {
    for (const [itemId, url] of this.itemIconUrls.entries()) {
      if (this.itemIconTextures.has(itemId)) continue;
      try {
        const tex = await Assets.load<Texture>(url);
        this.itemIconTextures.set(itemId, tex);
      } catch {
        // No icon → SeatItemFx still plays the floater.
      }
    }
  }

  // ---- internal: layout ------------------------------------------------

  private layoutSurface(
    cx: number,
    cy: number,
    dims: ReturnType<WheelResolveScene['pickDims']>,
  ): void {
    if (!this.disc || !this.hub || !this.rim) return;
    this.lastAvatarDiameter = dims.avatarDiameter;
    const diameter = dims.diameter;
    this.disc.x = cx;
    this.disc.y = cy;
    this.disc.setDiameter(diameter, dims.wedgeLabelRadius, dims.wedgeLabelFontSize);
    this.hub.x = cx;
    this.hub.y = cy;
    this.hub.setDiameter(dims.hubSize);
    this.rim.x = cx;
    this.rim.y = cy;
    this.rim.setDiameter(diameter + dims.rimGap);
    if (this.rimSprite) {
      this.rimSprite.x = cx;
      this.rimSprite.y = cy;
      // Producer feedback 2026-05-17 — new outer_rim.png is trimmed
      // (no transparent padding). The gold ring's inner hole takes
      // ~78% of the texture, so scale = 1 / 0.78 ≈ 1.28 puts the
      // rim's inner edge flush with the wedge disc outer edge —
      // no more dark gap between wedges and rim.
      const rimDiameter = Math.round(diameter * 1.28);
      this.rimSprite.width = rimDiameter;
      this.rimSprite.height = rimDiameter;
    }
    if (this.stakeRing) {
      this.stakeRing.x = cx;
      this.stakeRing.y = cy;
      this.stakeRing.setDiameter?.(dims.orbitRadius * 2 + 24);
    }
    // (central action-card layout retired 2026-05-27)
    if (this.centralLedger) {
      this.centralLedger.x = cx;
      this.centralLedger.y = cy;
    }
    if (this.confetti) {
      this.confetti.x = cx;
      this.confetti.y = cy;
    }
  }

  /**
   * Spawn a gesture float over a seat — the sprite rises + fades, the
   * wheel-game equivalent of the reel race's ReactionFloat (producer
   * 2026-06-08 "animate the hand"). Positioned in the fx layer at the
   * seat's live coords, so it tracks the avatar wherever it sits on the
   * rim. Advanced + reaped in {@link #tickerFn}. No-op without a texture
   * or fx layer.
   */
  private playGestureFloat(slot: number, texture: Texture | null): void {
    if (!texture || !this.fxContainer) return;
    const seat = this.seatPointers[slot - 1];
    if (!seat) return;
    const d = this.lastAvatarDiameter;
    const sprite = new Sprite(texture);
    sprite.anchor.set(0.5);
    sprite.width = Math.round(d * 0.9);
    sprite.height = Math.round(d * 0.9);
    sprite.x = seat.x;
    const baseY = seat.y - d * 0.5;
    sprite.y = baseY;
    this.fxContainer.addChild(sprite);
    this.gestureFloats.push({ sprite, elapsed: 0, baseY });
  }

  private refreshSeatPositions(): void {
    if (!this.latestState || !this.seatContainer) return;
    const { width, height } = this.app.renderer;
    const cx = width / 2;
    const cy = height / 2;
    const dims = this.pickDims(width, height);
    for (let i = 0; i < this.seatPointers.length; i++) {
      const angleDeg = SEAT_POINTER_OFFSETS_DEG[i] ?? 0;
      const angleRad = ((angleDeg - 90) * Math.PI) / 180;
      const px = cx + Math.cos(angleRad) * dims.orbitRadius;
      const py = cy + Math.sin(angleRad) * dims.orbitRadius;
      const seat = this.seatPointers[i]!;
      seat.x = px;
      seat.y = py;
    }
  }

  private refreshChipPositions(): void {
    if (!this.latestState || !this.chipContainer) return;
    const { width, height } = this.app.renderer;
    const cx = width / 2;
    const cy = height / 2;
    const dims = this.pickDims(width, height);
    // Chip sits clearly OUTSIDE the avatar. Producer feedback
    // 2026-05-17 ("Cards don't have the right padding"): the
    // previous code used a hardcoded 25px chipHalfH, but the chip
    // is now ~62px wide × ~31px tall, so at off-axis angles the
    // chip body overlapped the avatar. Use the MAX half-extent
    // (chip width / 2) to guarantee a clean gap on every angle.
    const chipHalfExtent = 62;
    const chipDist =
      dims.orbitRadius + dims.avatarDiameter / 2 + chipHalfExtent + 8;
    for (let i = 0; i < 6; i++) {
      const slot = i + 1;
      const chip = this.outcomeChips.get(slot);
      if (!chip) continue;
      const angleDeg = SEAT_POINTER_OFFSETS_DEG[i] ?? 0;
      const angleRad = ((angleDeg - 90) * Math.PI) / 180;
      chip.x = cx + Math.cos(angleRad) * chipDist;
      chip.y = cy + Math.sin(angleRad) * chipDist;
    }
  }

  // ---- internal: seats + chips ----------------------------------------

  private refreshSeats(
    state: WheelObservableState,
    selfSeatId: number | null,
  ): void {
    if (!this.seatContainer) return;
    const { width, height } = this.app.renderer;
    const cx = width / 2;
    const cy = height / 2;
    const dims = this.pickDims(width, height);
    const orbitRadius = dims.orbitRadius;
    const isStake = state.phase === 'STAKE';
    // Make sure seat avatars are sized for the current tier before
    // pushing per-seat state in. setDiameter is a no-op when the
    // tier hasn't changed since the previous tick.
    for (const seat of this.seatPointers) seat.setDiameter(dims.avatarDiameter);

    for (let i = 0; i < this.seatPointers.length; i++) {
      const slot = i + 1;
      const angleDeg = SEAT_POINTER_OFFSETS_DEG[i] ?? 0;
      const angleRad = ((angleDeg - 90) * Math.PI) / 180;
      const px = cx + Math.cos(angleRad) * orbitRadius;
      const py = cy + Math.sin(angleRad) * orbitRadius;

      const occupantEntry = Object.entries(
        state.seatPointerSlots ?? {},
      ).find(([, s]) => Number(s) === slot);
      const occupantSeatId = occupantEntry ? Number(occupantEntry[0]) : null;
      const occupantSeat = occupantSeatId
        ? state.seats.find((s) => s.seatId === occupantSeatId) ?? null
        : null;
      const pointer = this.seatPointers[i]!;
      const chip = this.outcomeChips.get(slot);

      if (!occupantSeat) {
        // Empty slot — show the dashed-ring "+" marker during
        // STAKE so the player has an affordance to claim it.
        // Producer feedback 2026-05-27 — also clear the avatar
        // from the previous occupant so a SELECT_SEAT swap doesn't
        // leave the player's face showing in their OLD slot.
        pointer.visible = isStake;
        pointer.x = px;
        pointer.y = py;
        pointer.clearOccupant();
        pointer.setEmptyMarker(isStake);
        chip?.hideImmediate();
        continue;
      }
      pointer.visible = true;
      pointer.setEmptyMarker(false);
      const seatState: WheelResolveSeatPointerState = {
        avatarTexture: this.avatarTextures.get(occupantSeat.seatId) ?? null,
        displayName: occupantSeat.displayName,
        pointerAngleDeg: angleDeg,
        isSelf: selfSeatId !== null && occupantSeat.seatId === selfSeatId,
        // U11 (2026-05-30) — keep the seat tappable in every phase so
        // the React-side {@code onSeatTap} can dispatch to SELECT_SEAT
        // during STAKE and to the ProfileCard during WHEEL_REVEAL /
        // WHEEL_RESOLVE. The phase branch lives in
        // {@code WheelResolveCanvas.tsx}; the seat just delivers the
        // tap. {@link attractSwap} keeps the dashed yellow pulse
        // STAKE-only so the spin moment isn't visually busy.
        interactive: true,
        attractSwap: isStake,
        // High-five badge — producer 2026-06-08. Shown for non-self
        // occupants during the spectating phases (WHEEL_REVEAL /
        // WHEEL_RESOLVE, i.e. not STAKE) so you can react without
        // opening the profile first. Audience (no self seat) gets none.
        showGesture:
          !isStake &&
          selfSeatId !== null &&
          occupantSeat.seatId !== selfSeatId,
      };
      pointer.setState(px, py, seatState);

      // Outcome chip — set data + reveal once the wheel lands.
      if (chip) {
        const outcome =
          state.dialResolveResult?.perSeatOutcomes?.[
            String(occupantSeat.seatId)
          ] ?? null;
        if (outcome) {
          chip.setData(occupantSeat.displayName, outcome);
        }
      }
    }

    this.refreshChipPositions();
  }

  // ---- internal: bonus markers + boost telegraph -----------------------

  private refreshBonusMarkers(
    state: WheelObservableState,
    selfSeatId: number | null,
  ): void {
    if (!this.bonusMarkerHost) return;
    const placements = state.bonusWedgePlacements ?? [];
    const { width, height } = this.app.renderer;
    const dims = this.pickDims(width, height);
    const markerDiameter = Math.max(20, Math.round(dims.diameter * 0.1));
    // Producer feedback 2026-05-17 — marker was sitting at the
    // wedge-label radius and obscuring the "×N" glyph. Drop it
    // inward toward the hub so the label stays readable above
    // the marker.
    const markerRadius = Math.round(dims.wedgeLabelRadius * 0.55);
    while (this.bonusMarkers.length < placements.length) {
      const m = new WheelResolveBonusMarker({
        diameter: markerDiameter,
        wedgeLabelRadius: markerRadius,
      });
      this.bonusMarkers.push(m);
      this.bonusMarkerHost.addChild(m);
    }
    while (this.bonusMarkers.length > placements.length) {
      const m = this.bonusMarkers.pop();
      m?.destroyDisplay();
    }
    for (let i = 0; i < placements.length; i++) {
      const placement = placements[i]!;
      const marker = this.bonusMarkers[i]!;
      const sourceSeatId = Number(placement.sourceSeatId);
      const sourceSeat = state.seats.find((s) => s.seatId === sourceSeatId);
      marker.setState({
        wedgeIndex: placement.wedgeIndex,
        avatarTexture: this.avatarTextures.get(sourceSeatId) ?? null,
        displayName: sourceSeat?.displayName ?? '',
        isSelf: selfSeatId !== null && sourceSeatId === selfSeatId,
      });
    }
  }

  private refreshDeployStrip(
    state: WheelObservableState,
    selfSeatId: number | null,
  ): void {
    if (!this.deployStrip) return;
    if (selfSeatId === null) {
      this.deployStrip.setTiles([], false);
      return;
    }
    const seat = state.seats.find((s) => s.seatId === selfSeatId);
    if (!seat) {
      this.deployStrip.setTiles([], false);
      return;
    }
    const owned = new Map<ItemId, number>();
    for (const id of seat.loadout) {
      owned.set(id, (owned.get(id) ?? 0) + 1);
    }
    const deployed = new Map<ItemId, number>();
    for (const id of seat.pendingDeployments) {
      deployed.set(id, (deployed.get(id) ?? 0) + 1);
    }
    const tiles: DeployStripTileData[] = Array.from(owned.entries()).map(
      ([id, count]) => ({
        itemId: id,
        iconTexture: this.itemIconTextures.get(id) ?? null,
        owned: count,
        deployed: deployed.get(id) ?? 0,
      }),
    );
    this.deployStrip.setTiles(tiles, seat.pendingDeployments.length > 0);
  }

  private refreshBoostTelegraph(
    state: WheelObservableState,
    selfSeatId: number | null,
  ): void {
    if (!this.boostTelegraph) return;
    if (selfSeatId === null) {
      this.boostTelegraph.visible = false;
      return;
    }
    const seat = state.seats.find((s) => s.seatId === selfSeatId);
    if (!seat) {
      this.boostTelegraph.visible = false;
      return;
    }
    const boostCount = seat.pendingDeployments.filter((id) => id === 'BOOST').length;
    const tripleBoostCount = seat.pendingDeployments.filter(
      (id) => id === 'TRIPLE_BOOST',
    ).length;
    this.boostTelegraph.setPegs(boostCount, tripleBoostCount);
    if (!this.boostTelegraph.visible) return;
    // Determine self pointer slot to place the badge outward.
    const slotEntry = Object.entries(state.seatPointerSlots ?? {}).find(
      ([k]) => Number(k) === selfSeatId,
    );
    const slot = slotEntry ? Number(slotEntry[1]) : 1;
    const pointerIdx = Math.max(0, Math.min(5, slot - 1));
    const angleDeg = SEAT_POINTER_OFFSETS_DEG[pointerIdx] ?? 0;
    this.boostTelegraph.setRadialOffset(angleDeg);
    const pointer = this.seatPointers[pointerIdx]!;
    // Reparent the telegraph into the self-pointer so it follows
    // the seat's position. Keep it BENEATH the avatar so it tucks
    // behind the portrait (producer brief 2026-05-16 — z-order
    // visual fix).
    if (this.boostTelegraphHost !== pointer) {
      if (this.boostTelegraphHost) {
        // Remove from previous host without destroying.
        this.boostTelegraph.parent?.removeChild(this.boostTelegraph);
      }
      pointer.addChildAt(this.boostTelegraph, 0);
      this.boostTelegraphHost = pointer;
    }
  }

  // ---- internal: spin + chip reveal + cards -----------------------------

  private startSpin(state: WheelObservableState): void {
    const resolve = state.dialResolveResult;
    if (!resolve || !this.disc) return;
    // Only mark the spin as started once we know we'll actually
    // schedule the tween (i.e. we have a disc + resolve). Earlier
    // versions set spinStarted at the call site, which made a
    // before-init setState call a permanent no-op for the round.
    this.spinStarted = true;
    const baselineRotation =
      SPIN_REVOLUTIONS * 360 - resolve.baselineLandingAngleDeg;
    const finalRotation =
      SPIN_REVOLUTIONS * 360 - resolve.landingAngleDeg;

    let lastTickRotation = 0;
    const TICK_PER_DEG = 18;
    this.tweens.start({
      durationMs: SPIN_DURATION_MS,
      easing: Easings.easeOutQuart,
      onUpdate: (p) => {
        const r = p * baselineRotation;
        this.setDiscRotation(r);
        if (
          Math.floor(r / TICK_PER_DEG) >
          Math.floor(lastTickRotation / TICK_PER_DEG)
        ) {
          AudioBus.shared.play('wheel:tick', { volume: 0.35 });
          lastTickRotation = r;
        }
      },
      onComplete: () => {
        AudioBus.shared.play('wheel:land', { volume: 0.7 });
        const magnetDelta =
          resolve.baselineLandingAngleDeg - resolve.landingAngleDeg;
        if (Math.abs(magnetDelta) > 0.5) {
          this.playMagnetPull(baselineRotation, finalRotation, () => {
            // Producer feedback 2026-05-27 — "It's still not possible
            // to read the final ledger. It only shows for about
            // 500ms." Root cause: revealChips() was firing only AFTER
            // the action-card sequence finished (1.8s × N cards), so
            // a chaotic round with ~10 cards pushed the ledger reveal
            // to ~14s into PAYOUT, leaving sub-second visibility.
            // Reveal the ledger AS SOON AS the wheel (and magnet
            // pull) lands — the rim-avatar seat FX play in parallel
            // on the seat avatars around the perimeter and don't
            // overlap with the central ledger panel.
            this.revealChips();
            this.beginPostSpinSequence(resolve.actionCards);
          });
        } else {
          this.revealChips();
          this.beginPostSpinSequence(resolve.actionCards);
        }
      },
    });
  }

  private playMagnetPull(
    from: number,
    to: number,
    onDone: () => void,
  ): void {
    let delta = to - from;
    const mod360 = ((delta % 360) + 360) % 360;
    delta = mod360 > 180 ? mod360 - 360 : mod360;
    if (Math.abs(delta) < 0.5) {
      onDone();
      return;
    }
    const start = from;
    const end = from + delta;
    AudioBus.shared.play('wheel:tick', { volume: 0.55 });
    this.tweens.start({
      durationMs: 700,
      easing: Easings.easeOutBack,
      onUpdate: (p) => {
        this.setDiscRotation(start + p * (end - start));
      },
      onComplete: () => {
        AudioBus.shared.play('wheel:land', { volume: 0.5 });
        this.setDiscRotation(to);
        onDone();
      },
    });
  }

  private beginPostSpinSequence(cards: ReadonlyArray<WheelActionCard>): void {
    // Filter: skip per-rival sibling cards (target ≠ null on
    // mass-effect items — they exist for OutcomeChip ledger /
    // SeatItemFx but the overlay only plays the summary card).
    const display: WheelActionCard[] = [];
    for (const card of cards) {
      if (card.itemId === 'LIGHTNING' && card.targetSeatId !== null) continue;
      if (card.itemId === 'POW' && card.targetSeatId !== null) continue;
      // Single-player items only play on the deployer's own screen.
      if (
        (card.itemId === 'BOOST' ||
          card.itemId === 'TRIPLE_BOOST' ||
          card.itemId === 'X2_BONUS' ||
          card.itemId === 'COIN_STASH') &&
        this.selfSeatId !== null &&
        card.sourceSeatId !== this.selfSeatId
      ) {
        continue;
      }
      display.push(card);
    }
    this.actionCardQueue = display;
    if (display.length === 0) {
      // Empty queue → skip straight to chip reveal + confetti.
      this.revealChips();
      return;
    }
    this.playNextActionCard(cards);
  }

  private playNextActionCard(allCards: ReadonlyArray<WheelActionCard>): void {
    if (this.actionCardTimer !== null) {
      window.clearTimeout(this.actionCardTimer);
      this.actionCardTimer = null;
    }
    const next = this.actionCardQueue.shift();
    if (!next) {
      this.revealChips();
      return;
    }
    // Producer feedback 2026-05-27 — central action card retired.
    // Each card now drives ONLY the per-seat fx (icon on victim +
    // coin floater on attacker & victim) for the 1800ms slot.
    this.playSeatFxForCard(next, allCards);
    this.playAttackFxForCard(next);

    this.actionCardTimer = window.setTimeout(() => {
      this.playNextActionCard(allCards);
    }, ACTION_CARD_DURATION_MS);
  }

  private playSeatFxForCard(
    card: WheelActionCard,
    allCards: ReadonlyArray<WheelActionCard>,
  ): void {
    if (!this.fxContainer) return;
    const affected = this.affectedSeatsForCard(card);
    for (const seatId of affected) {
      const slot = this.findSlotForSeat(seatId);
      if (slot === null) continue;
      const pointer = this.seatPointers[slot - 1]!;
      // Look up existing SeatItemFx for this seat — reuse to avoid
      // a flicker if two cards land in quick succession.
      let rec = this.activeSeatFx.get(seatId);
      if (!rec) {
        const fx = new WheelResolveSeatFx({
          avatarDiameter: 64,
          coinTexture: this.coinTexture,
        });
        this.fxContainer.addChild(fx);
        rec = { fx, card };
        this.activeSeatFx.set(seatId, rec);
      }
      rec.fx.x = pointer.x;
      rec.fx.y = pointer.y;
      rec.card = card;
      const seatDelta = this.lookupDeltaForSeat(card, seatId, allCards);
      const isSource = card.sourceSeatId === seatId;
      const isTarget = card.targetSeatId === seatId;
      // Producer feedback 2026-05-26 / 2026-05-27 — "the item should
      // only be shown on the victim, not the attacker." Suppress the
      // icon on the SOURCE seat for any attack item. LIGHTNING and
      // POW are mass attacks where the summary card carries
      // targetSeatId=null, so we can't gate on hasVictim alone —
      // those items are ALWAYS attacks and the source never shows
      // their icon, regardless of which card variant fired.
      // Self-buff items (BOOST, COIN_STASH, MAGNET, X2_BONUS,
      // BONUS_WEDGE) still show the icon on the deployer.
      const isAttackItem =
        card.itemId === 'HIT' ||
        card.itemId === 'BOMB' ||
        card.itemId === 'SHAKEDOWN' ||
        card.itemId === 'LIGHTNING' ||
        card.itemId === 'POW';
      const suppressIcon = isSource && isAttackItem;
      const cfg: SeatFxPlayConfig = {
        itemId: card.itemId,
        iconTexture: suppressIcon
          ? null
          : this.itemIconTextures.get(card.itemId) ?? null,
        seatDelta,
        isSource,
        isTarget,
        blocked: card.blocked,
      };
      rec.fx.play(cfg);
    }
  }

  /** Stage-wide motion for attack items, on top of the per-seat icon +
   *  coin floater: chain lightning from the attacker to every rival,
   *  impact bursts for bomb / hit / shakedown / pow, and a screen shake
   *  sized to the hit. */
  private playAttackFxForCard(card: WheelActionCard): void {
    const fx = this.attackFx;
    if (!fx) return;
    // Keep the motion above the central ledger and chips.
    this.app.stage.addChild(fx);
    const posOf = (seatId: number): { x: number; y: number } | null => {
      const slot = this.findSlotForSeat(seatId);
      if (slot === null) return null;
      const p = this.seatPointers[slot - 1];
      return p ? { x: p.x, y: p.y } : null;
    };
    const rivals = (): { x: number; y: number }[] =>
      this.affectedSeatsForCard(card)
        .filter((id) => id !== card.sourceSeatId)
        .map(posOf)
        .filter((p): p is { x: number; y: number } => p !== null);
    switch (card.itemId) {
      case 'LIGHTNING': {
        if (card.targetSeatId !== null) return; // per-rival sibling card
        const from = posOf(card.sourceSeatId);
        if (!from) return;
        // Hop seat to seat clockwise around the rim from the attacker,
        // so each bolt is a short chord near the edge rather than a
        // line through the ledger in the middle of the wheel.
        const { width, height } = this.app.renderer;
        const cx = this.disc?.x ?? width / 2;
        const cy = this.disc?.y ?? height / 2;
        const base = Math.atan2(from.y - cy, from.x - cx);
        const around = (p: { x: number; y: number }): number =>
          (Math.atan2(p.y - cy, p.x - cx) - base + Math.PI * 4) % (Math.PI * 2);
        const targets = rivals().sort((a, b) => around(a) - around(b));
        if (targets.length === 0) return;
        fx.strike({
          from,
          targets,
          chain: true,
          center: { x: cx, y: cy },
          viewport: { width, height },
          onImpact: (i) => this.shake(i === 0 ? 9 : 4, i === 0 ? 320 : 180),
        });
        return;
      }
      case 'POW': {
        if (card.targetSeatId !== null) return;
        fx.burst({
          at: rivals(),
          palette: 'gold',
          staggerMs: 80,
          onImpact: (i) => {
            if (i === 0) this.shake(6, 260);
          },
        });
        return;
      }
      case 'BOMB':
      case 'HIT':
      case 'SHAKEDOWN': {
        if (card.targetSeatId === null) return;
        const at = posOf(card.targetSeatId);
        if (!at) return;
        if (card.blocked) {
          fx.burst({ at: [at], palette: 'white', scale: 0.7 });
          return;
        }
        const bomb = card.itemId === 'BOMB';
        fx.burst({
          at: [at],
          palette: bomb ? 'fire' : 'white',
          scale: bomb ? 1.8 : 1,
          onImpact: () => this.shake(bomb ? 11 : 5, bomb ? 380 : 220),
        });
        return;
      }
      default:
        return;
    }
  }

  /** Shake the whole stage, decaying to rest over durationMs. A new
   *  shake replaces one in progress. */
  private shake(amplitude: number, durationMs: number): void {
    this.stopShake();
    const stage = this.app.stage;
    const start = performance.now();
    const step = (now: number): void => {
      const p = (now - start) / durationMs;
      if (p >= 1) {
        this.stopShake();
        return;
      }
      const a = amplitude * (1 - p) * (1 - p);
      stage.position.set((Math.random() * 2 - 1) * a, (Math.random() * 2 - 1) * a);
      this.shakeRaf = requestAnimationFrame(step);
    };
    this.shakeRaf = requestAnimationFrame(step);
  }

  private stopShake(): void {
    if (this.shakeRaf !== null) cancelAnimationFrame(this.shakeRaf);
    this.shakeRaf = null;
    this.app?.stage?.position.set(0, 0);
  }

  private affectedSeatsForCard(card: WheelActionCard): number[] {
    const state = this.latestState;
    if (!state) return [];
    switch (card.itemId) {
      case 'BOOST':
      case 'TRIPLE_BOOST':
      case 'MAGNET':
      case 'X2_BONUS':
      case 'COIN_STASH':
      case 'BONUS_WEDGE':
        return [card.sourceSeatId];
      case 'HIT':
      case 'BOMB':
        // Producer feedback 2026-05-26 — "the item should only be
        // shown on the victim, not the attacker." Deployer gets a
        // FLOATER (gain) but no icon — handled via the
        // suppress-icon-on-source flag in playSeatFxForCard below.
        return card.targetSeatId !== null
          ? [card.sourceSeatId, card.targetSeatId]
          : [];
      case 'SHAKEDOWN':
        return card.targetSeatId !== null
          ? [card.sourceSeatId, card.targetSeatId]
          : [];
      case 'POW':
        return state.seats
          .filter(
            (s) => s.seatId !== card.sourceSeatId && s.kind !== 'EMPTY',
          )
          .map((s) => s.seatId);
      case 'LIGHTNING':
        return state.seats
          .filter((s) => s.kind !== 'EMPTY')
          .map((s) => s.seatId);
      default:
        return [];
    }
  }

  private lookupDeltaForSeat(
    card: WheelActionCard,
    seatId: number,
    allCards: ReadonlyArray<WheelActionCard>,
  ): number {
    if (card.blocked) return 0;
    if (card.sourceSeatId === seatId && card.sourceDelta !== 0) {
      return card.sourceDelta;
    }
    if (card.targetSeatId === seatId && card.targetDelta !== 0) {
      return card.targetDelta;
    }
    if (
      (card.itemId === 'LIGHTNING' || card.itemId === 'POW') &&
      card.targetSeatId === null
    ) {
      const sibling = allCards.find(
        (c) =>
          c.itemId === card.itemId &&
          c.sourceSeatId === card.sourceSeatId &&
          c.targetSeatId === seatId,
      );
      if (sibling) return sibling.targetDelta;
    }
    return 0;
  }

  private findSlotForSeat(seatId: number): number | null {
    if (!this.latestState) return null;
    const entry = Object.entries(this.latestState.seatPointerSlots ?? {}).find(
      ([k]) => Number(k) === seatId,
    );
    if (!entry) return null;
    return Number(entry[1]);
  }

  private clearAllSeatFx(): void {
    this.attackFx?.stop();
    this.stopShake();
    for (const rec of this.activeSeatFx.values()) {
      rec.fx.hide();
      rec.fx.destroyDisplay();
    }
    this.activeSeatFx.clear();
  }

  private clearActionCardQueue(): void {
    this.actionCardQueue = [];
    if (this.actionCardTimer !== null) {
      window.clearTimeout(this.actionCardTimer);
      this.actionCardTimer = null;
    }
  }

  private revealChips(): void {
    if (this.chipsRevealed) return;
    this.chipsRevealed = true;
    const state = this.latestState;
    const resolve = state?.dialResolveResult;
    if (!state || !resolve) return;

    // Producer feedback 2026-05-27 — replace the perimeter chip
    // grid with a personal central ledger for the local player.
    // Audience mode (selfSeatId null) falls back to the legacy chip
    // reveal so spectators still see who landed where.
    if (this.selfSeatId !== null) {
      const selfSeatId = this.selfSeatId;
      const outcome = resolve.perSeatOutcomes[String(selfSeatId)];
      const selfSeat = state.seats.find((s) => s.seatId === selfSeatId);
      if (outcome && selfSeat && this.centralLedger) {
        const stake = selfSeat.pendingStake ?? 0;
        // Producer brief 2026-05-27 — ledger comes from the server
        // when present; client builder is the fallback for the
        // pre-2026-05-27 SSE shape (empty map → undefined lookup).
        const serverLines =
          resolve.ledgerLinesBySeat?.[String(selfSeatId)];
        const lineItems = serverLines
          ? renderServerLedgerLines(serverLines, state.seats)
          : buildLedgerLineItems(
              resolve.actionCards,
              selfSeatId,
              state.seats,
            );
        // Reconcile the stake-row subtotal so WINNINGS (subtotal + Σ
        // line deltas) equals the player's true, non-negative take.
        // Using the raw payout here double-counts every item line and
        // can read negative (see reconcileLedgerSubtotal). The fallback
        // path (no server lines) peels the rendered deltas the same way.
        const subtotal = serverLines
          ? reconcileLedgerSubtotal(outcome.payout, serverLines)
          : Math.max(0, outcome.payout) -
            lineItems.reduce((sum, li) => sum + li.delta, 0);
        const podiumRank = projectPodiumRank(state, selfSeatId);
        this.centralLedger.reveal({
          stake,
          wedge: outcome.wedge,
          subtotal,
          lineItems,
          podiumRank,
        });
        AudioBus.shared.play('chip:drop', { volume: 0.5 });
      }
    } else {
      // Audience-mode fallback: stagger chip reveals as before.
      const entries = Object.entries(resolve.perSeatOutcomes)
        .map(([seatIdStr, o]) => ({
          seatId: Number(seatIdStr),
          outcome: o,
        }))
        .sort((a, b) => b.outcome.payout - a.outcome.payout);
      this.chipContainer && (this.chipContainer.visible = true);
      entries.forEach(({ seatId }, idx) => {
        const slot = this.findSlotForSeat(seatId);
        if (slot === null) return;
        const chip = this.outcomeChips.get(slot);
        if (!chip) return;
        window.setTimeout(() => {
          chip.reveal();
          AudioBus.shared.play('chip:drop', { volume: 0.5 });
        }, idx * CHIP_REVEAL_STAGGER_MS);
      });
    }

    // Confetti — kick off near the start of the reveal.
    window.setTimeout(() => {
      this.confetti?.burst(50);
      AudioBus.shared.play('wheel:ceremony', { volume: 0.6 });
    }, 200);
  }

  private setDiscRotation(deg: number): void {
    this.disc?.setRotation(deg);
    // Producer feedback 2026-05-17 — bonus markers are now locked
    // to their wedge like a physical sticker on a real wheel. They
    // sit inside the disc's rotatingOverlay so the disc's setRotation
    // already rotates them. No counter-rotation needed.
    void this.bonusMarkers;
  }
}
